<!-- src/app/hours/components/hours/hours.component.html -->
<div class="hours-page-wrapper">
  <div *ngIf="isLoading" class="loading-overlay" aria-live="polite" aria-busy="true">
    <div class="spinner"></div>
  </div>

  <div *ngIf="errorMessage" class="error-message" role="alert">
    {{ errorMessage }}
  </div>

  <ng-container *ngIf="vm$ | async as vm">
    <div class="top-bar" *ngIf="!isLoading">
      <div class="top-left"></div>

      <h2 class="week-label">
        Week {{ vm.week.fullYearWeek }}
        <span class="dates">({{ vm.week.startDate }} - {{ vm.week.endDate }})</span>
      </h2>

      <div class="top-actions">
        <button
          class="action-btn subtle"
          type="button"
          *ngIf="vm.dirty && !vm.isPayrollSynced"
          (click)="cancelChanges()"
        >
          Cancel
        </button>

        <button
          class="action-btn primary"
          type="button"
          *ngIf="vm.dirty && !vm.isPayrollSynced"
          (click)="save()"
        >
          Save
        </button>
      </div>
    </div>

    <div class="hours-legend" *ngIf="!isLoading">
      <div class="legend-item">
        <span class="swatch approved"></span>
        <span>Approved</span>
      </div>

      <div class="legend-item">
        <span class="swatch match"></span>
        <span>Clocked = Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch over-booked"></span>
        <span>Clocked &gt; Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch under-booked"></span>
        <span>Clocked &lt; Booked</span>
      </div>

      <div class="legend-item">
        <span class="swatch missing-punch"></span>
        <span>Missing Punch</span>
      </div>

      <div class="legend-item">
        <span class="swatch paycode-holiday"></span>
        <span>Holiday</span>
      </div>

      <div class="legend-item">
        <span class="swatch requested-offtime"></span>
        <span>Requested Offtime</span>
      </div>
    </div>

    <div class="content-shell" *ngIf="!isLoading">
      <section
        class="wons-surface"
        [class.locked-section]="vm.isPayrollSynced"
        aria-label="Work order booking"
      >
        <div class="selected-day-lock-note" *ngIf="vm.isPayrollSynced && currentSelectionLockReason">
          {{ currentSelectionLockReason }}
        </div>

        <div class="wons-grid" role="list" *ngIf="vm.wonCards.length; else noWonsTpl">
          <div
            class="won-card"
            role="listitem"
            *ngFor="let won of vm.wonCards"
            [class.locked-section]="vm.isPayrollSynced || isWonLockedForSelectedDate(won.wonId)"
            [class.approved-won-locked]="!vm.isPayrollSynced && isWonLockedForSelectedDate(won.wonId)"
            [attr.title]="
              vm.isPayrollSynced
                ? vm.payrollLockedMessage
                : getWonLockReasonForSelectedDate(won.wonId)
            "
          >
            <div class="won-quick-actions" aria-hidden="false">
              <button
                *ngIf="showQuickFill(vm) && !vm.isPayrollSynced && !isWonLockedForSelectedDate(won.wonId)"
                class="quick-btn quick-btn--fill"
                type="button"
                (click)="quickFillToWon(vm, won.wonId)"
                aria-label="Fill remaining clocked hours to this work order"
                title="Fill remaining clocked hours"
              >
                <span class="quick-btn__icon">✓</span>
              </button>

              <button
                class="quick-btn quick-btn--clear"
                type="button"
                [disabled]="
                  won.selectedDayBookedHours <= 0.0001 ||
                  vm.isPayrollSynced ||
                  isWonLockedForSelectedDate(won.wonId)
                "
                (click)="clearDayForWon(won.wonId)"
                aria-label="Clear this work order for the selected day"
                title="Clear this work order for the selected day"
              >
                <span class="quick-btn__icon">−</span>
              </button>
            </div>

            <div class="won-top">
              <div class="won-meta">
                <div class="won-number">{{ won.won }}</div>
                <div class="won-desc" *ngIf="won.description">{{ won.description }}</div>
              </div>

              <div class="won-mid">
                <div class="day-booked" aria-label="Selected day booked hours">
                  <div class="label">{{ getSelectedDayLabel(vm.selectedDate) }}</div>
                  <div class="value">{{ won.selectedDayBookedHours | number: '1.2-2' }}</div>
                </div>
              </div>

              <div class="won-week-total" aria-label="Week total booked hours">
                <div class="label">Week</div>
                <div class="value">{{ won.weekTotalBookedHours | number: '1.2-2' }}</div>
              </div>
            </div>

            <div class="won-controls" aria-label="Hour controls">
              <div class="controls-row controls-row--minus">
                <button
                  class="chip chip--minus"
                  type="button"
                  [disabled]="
                    won.selectedDayBookedHours <= 0.0001 ||
                    vm.isPayrollSynced ||
                    isWonLockedForSelectedDate(won.wonId)
                  "
                  (click)="subtractHours(won.wonId, 0.25)"
                >
                  −0.25
                </button>

                <button
                  class="chip chip--minus"
                  type="button"
                  [disabled]="
                    won.selectedDayBookedHours <= 0.0001 ||
                    vm.isPayrollSynced ||
                    isWonLockedForSelectedDate(won.wonId)
                  "
                  (click)="subtractHours(won.wonId, 1)"
                >
                  −1
                </button>

                <button
                  class="chip chip--minus"
                  type="button"
                  [disabled]="
                    won.selectedDayBookedHours <= 0.0001 ||
                    vm.isPayrollSynced ||
                    isWonLockedForSelectedDate(won.wonId)
                  "
                  (click)="subtractHours(won.wonId, 2)"
                >
                  −2
                </button>

                <button
                  class="chip chip--minus"
                  type="button"
                  [disabled]="
                    won.selectedDayBookedHours <= 0.0001 ||
                    vm.isPayrollSynced ||
                    isWonLockedForSelectedDate(won.wonId)
                  "
                  (click)="subtractHours(won.wonId, 5)"
                >
                  −5
                </button>
              </div>

              <div class="controls-row controls-row--plus">
                <button
                  class="chip chip--plus"
                  type="button"
                  [disabled]="vm.isPayrollSynced || isWonLockedForSelectedDate(won.wonId)"
                  (click)="addHours(won.wonId, 0.25)"
                >
                  +0.25
                </button>

                <button
                  class="chip chip--plus"
                  type="button"
                  [disabled]="vm.isPayrollSynced || isWonLockedForSelectedDate(won.wonId)"
                  (click)="addHours(won.wonId, 1)"
                >
                  +1
                </button>

                <button
                  class="chip chip--plus"
                  type="button"
                  [disabled]="vm.isPayrollSynced || isWonLockedForSelectedDate(won.wonId)"
                  (click)="addHours(won.wonId, 2)"
                >
                  +2
                </button>

                <button
                  class="chip chip--plus"
                  type="button"
                  [disabled]="vm.isPayrollSynced || isWonLockedForSelectedDate(won.wonId)"
                  (click)="addHours(won.wonId, 5)"
                >
                  +5
                </button>
              </div>
            </div>
          </div>
        </div>

        <ng-template #noWonsTpl>
          <div class="no-wons">No work orders found for your account.</div>
        </ng-template>
      </section>
    </div>

    <div class="week-bar" *ngIf="!isLoading" aria-label="Week navigation">
      <div class="week-bar-shell" #weekBarShell>
        <button
          class="week-nav-tile"
          type="button"
          (click)="goPrevious()"
          [disabled]="!canGoPrevious() || isLoading"
          [attr.aria-label]="isMobileView ? 'Previous day' : 'Previous week'"
        >
          <div class="arrow">←</div>
          <div class="txt">{{ isMobileView ? 'Prev Day' : 'Prev Week' }}</div>
        </button>

        <div class="week-bar-inner">
          <div
            class="day-tile"
            *ngFor="let day of getMobileVisibleDays(vm)"
            [ngClass]="{
              selected: day.isSelected,
              today: !day.isSelected && day.isToday,
              approved: day.status === 'approved',
              locked: day.isLocked,
              match: day.status === 'match',
              'over-booked': day.status === 'over-booked',
              'under-booked': day.status === 'under-booked',
              'missing-punch': day.status === 'missing-punch',
              'paycode-holiday': day.status === 'paycode-holiday',
              'requested-offtime': day.status === 'requested-offtime',
              none: day.status === 'none'
            }"
            (click)="selectDay(day.date)"
            role="button"
            tabindex="0"
          >
            <div class="day-label">{{ day.label }}</div>
            <div class="day-date">{{ formatBottomDate(day.date) }}</div>
            <div class="day-lock-pill" *ngIf="day.isLocked">Locked</div>

            <div class="day-hours">
              <div class="row booked">
                <span class="k">Booked</span>
                <span class="v">{{ day.booked | number: '1.2-2' }}</span>
              </div>

              <div class="row clocked">
                <span class="k">Clocked</span>
                <span class="v">{{ day.clocked | number: '1.2-2' }}</span>
              </div>

              <div class="row scheduled">
                <span class="k">Sched</span>
                <span class="v">
                  {{ day.scheduledStart && day.scheduledEnd ? day.scheduledStart + ' - ' + day.scheduledEnd : '—' }}

                  <span style="font-size: x-small; display: block; text-align: right;">
                    {{ day.breakTime > 0 ? '(break ' + (day.breakTime | number: '1.2-2') + ')' : '' }}
                  </span>
                </span>
              </div>

              @if (day.timelogs.length > 0) {
                <div class="punches-scroll">
                  <div class="row punches punches-title">
                    <span class="k">Punches</span>
                  </div>

                  @for (timelog of day.timelogs; track timelog.id) {
                    <div class="punch-entry">
                      <div class="row punches">
                        <span class="v">{{ formatPunchTimeRange(timelog) }}</span>
                      </div>

                      <div class="row punch-timezone">
                        <span class="timezone-id">{{ getPunchTimeZoneLabel(timelog) }}</span>
                      </div>
                    </div>
                  }
                </div>
              }
            </div>
          </div>
        </div>

        <button
          class="week-nav-tile"
          type="button"
          (click)="goNext()"
          [disabled]="!canGoNext() || isLoading"
          [attr.aria-label]="isMobileView ? 'Next day' : 'Next week'"
        >
          <div class="arrow">→</div>
          <div class="txt">{{ isMobileView ? 'Next Day' : 'Next Week' }}</div>
        </button>
      </div>
    </div>
  </ng-container>
</div>